import { Combobox, ErrorState, toast } from '@berky/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { findModule } from '../modules'
import {
  usePlatformApi,
  type PlattformNutzer,
  type VerwaltungsRolle,
  type VerzeichnisPerson,
} from './api'
export function moduleTitle(id: string) {
  return (
    findModule(id)?.title ?? { platform: 'Plattform', feedback: 'Feedback' }[id] ?? id
  )
}
export function roleTitle(role: string) {
  const suffix = role.slice(role.indexOf('.') + 1)
  return (
    {
      admin: 'Administration',
      nutzer: 'Nutzer',
      bearbeiter: 'Bearbeitung',
      freigeber: 'Freigabe',
    }[suffix] ?? suffix
  )
}
export function groupRoles(roles: string[], catalog: VerwaltungsRolle[]) {
  const groups = new Map<string, string[]>()
  for (const role of [...new Set(roles)]) {
    const module =
      catalog.find((item) => item.name === role)?.moduleId ?? role.split('.')[0]!
    groups.set(module, [...(groups.get(module) ?? []), role])
  }
  return [...groups.entries()].sort(([a], [b]) =>
    moduleTitle(a).localeCompare(moduleTitle(b), 'de'),
  )
}
export function useDebounced(value: string) {
  const [result, setResult] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setResult(value), 300)
    return () => clearTimeout(timer)
  }, [value])
  return result
}
export function message(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'Die Änderung konnte nicht gespeichert werden.'
}
export function PersonPicker({
  onSelect,
}: {
  onSelect: (person: PlattformNutzer) => void
}) {
  const api = usePlatformApi()
  const client = useQueryClient()
  const [search, setSearch] = useState('')
  const term = useDebounced(search)
  const query = useQuery({
    queryKey: ['verwaltung', 'directory-search', term],
    queryFn: ({ signal }) =>
      api.get<{ items: VerzeichnisPerson[] }>('/verzeichnis/personen', {
        query: { suche: term },
        signal,
      }),
  })
  const add = useMutation({
    mutationFn: async (person: VerzeichnisPerson) => {
      const result = await api.post<{ id: string }>('/users/aus-verzeichnis', person)
      const users = await api.post<PlattformNutzer[]>('/users/lookup', [
        person.externalId,
      ])
      return (
        users.find((item) => item.id === result.id) ?? {
          ...person,
          id: result.id,
          roles: [],
          isBlocked: false,
        }
      )
    },
    onSuccess: async (person) => {
      await client.invalidateQueries({ queryKey: ['verwaltung', 'permissions'] })
      onSelect(person)
    },
    onError: (error) => toast.error(message(error)),
  })
  return (
    <div className="space-y-2">
      <Combobox
        items={query.data?.items ?? []}
        value={null}
        onChange={(person) => person && add.mutate(person)}
        onSearch={setSearch}
        getKey={(person) => person.externalId}
        getLabel={(person) => person.name}
        getDescription={(person) => person.email}
        placeholder="Person im Firmenverzeichnis suchen"
        emptyText="Keine passende Person gefunden"
        loading={query.isFetching || add.isPending}
      />
      {query.isError && (
        <ErrorState
          description="Das Firmenverzeichnis ist nicht erreichbar."
          onRetry={() => void query.refetch()}
        />
      )}
    </div>
  )
}
